import { createContext, useContext, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styles from './DetailList.module.css';

/**
 * 🔒 The detail list (piece 4; T177): the one function every list of rows that
 * is not a table goes through. The product owner, 2026-09-22: *"Review all items
 * that can be a function like list view and title bars and general page
 * construction … Id prefer they be more function since the change to one
 * propagates opposed to us having to check each one."*
 *
 * 🔨 Two looks and no third, on the owner's *"build both"* and *"Unification 1
 * can be 2 functions just not many"*. `separator` is the settings look — rows
 * divided by a hairline over the card's own ground. `card` is the banking and
 * accounting look — each row its own padded surface. The list sets the look once
 * and the rows inherit it.
 *
 * 🔨 It is not the table. Every sortable, pageable, column-configured list goes
 * through List Viewer, and a record page's summary rows are the Tile's (piece 4).
 * This is the readout list: a card's worth of rows, each a title, a detail line
 * or two, its badges and its own buttons.
 */
export type DetailListLook = 'separator' | 'card';

/** Every `data-*` attribute a caller passes lands on the element verbatim, because a suite reads the row by it. */
export type RowDataAttributes = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

export interface DetailListProps extends RowDataAttributes {
  /** The look every row inside inherits. */
  look: DetailListLook;
  /** The accessible name of the list, where the card's heading does not already give it one. */
  label?: string;
  children: ReactNode;
}

/**
 * 🔒 One labelled middle column of a row (T3227): a share, a time, a date, a
 * status word. It is drawn as the document panels drew theirs, right-aligned in
 * figures from the tablet width up, and stacked on its own line beneath the title
 * block below it, where its label reads before its value.
 */
export interface DetailColumn extends RowDataAttributes {
  /**
   * The column's name, read before the value where the row stacks. 🔨 It is
   * absent on a column the row never named, so moving a panel onto the row adds
   * no word a person reads.
   */
  label?: string;
  /** The accessible name of the cell, where its value alone does not say what it is. */
  ariaLabel?: string;
  value: ReactNode;
}

export interface DetailRowProps extends RowDataAttributes {
  /** Set only where a row stands outside a list of its own; otherwise the list's look reaches it. */
  look?: DetailListLook;
  /**
   * The row's own first line. It is absent only on a row that is a sentence
   * rather than a thing — the in-list line that says the list is empty.
   */
  title?: ReactNode;
  /**
   * A line directly under the title, in the row's own type rather than the
   * muted detail's: a posted entry's memo, which is its own sentence and not a
   * caption about it (decision 0466).
   */
  lead?: ReactNode;
  /** One line under the title, or several: an array draws one muted line each. */
  detail?: ReactNode | readonly ReactNode[];
  /** A destination for the title, which draws it as a link; the row itself stays unclickable. */
  href?: string;
  /**
   * A bare checkbox that selects the row. 🔨 It and the title block share one
   * label, so pressing the title selects the row — which is what a person
   * picking twenty bills to pay actually presses.
   * 🔨 A control that carries a label of its own goes in `leading` instead: a
   * label inside a label announces neither.
   */
  pick?: ReactNode;
  /** A control of the row's own standing at its left, before the title block, with a label of its own. */
  leading?: ReactNode;
  /**
   * The row's own control inside the title block, under the detail lines: the
   * reason a resolve needs, the note a close carries. 🔨 It goes here rather
   * than beside the block because that is where every page drew it, and a
   * field pushed out to the row's own line changes the row's height.
   */
  field?: ReactNode;
  /**
   * The pills that follow the title block. 🔨 They are placed beside the main
   * block rather than inside it, which is where every page drew them, so the row
   * does not grow a line when a status arrives.
   */
  badges?: ReactNode;
  /**
   * 🔒 The row's labelled middle columns, drawn straight after the title block
   * (T3227). None by default, so a row that passes none draws as it always did.
   */
  columns?: readonly DetailColumn[];
  /**
   * The row's own buttons, at its right. 🔨 They are drawn unwrapped, spaced by
   * the row's own gap: a wrapper with a gap of its own re-spaced the separator
   * look's buttons, which is a pixel the sweep is not allowed to move.
   */
  actions?: ReactNode;
  /** A column of the row's own between the title block and the buttons: an amount, a pair of balances, an inline form. */
  children?: ReactNode;
  /**
   * A block that wraps onto a line of its own beneath everything else, because
   * it is a whole width wide: a posted entry's own debit and credit lines, a
   * return's periods. 🔨 It comes after the buttons so the buttons stay on the
   * row's first line, which is where the pages drew them.
   */
  expansion?: ReactNode;
}

export const detailListDocumentedStates = ['separator', 'card'] as const;

const LookContext = createContext<DetailListLook>('separator');

const LIST_LOOK: Readonly<Record<DetailListLook, string>> = { separator: styles.listSeparator!, card: styles.listCard! };
const ROW_LOOK: Readonly<Record<DetailListLook, string>> = { separator: styles.rowSeparator!, card: styles.rowCard! };
const TITLE_LOOK: Readonly<Record<DetailListLook, string>> = { separator: styles.titleSeparator!, card: styles.titleCard! };
const DETAIL_LOOK: Readonly<Record<DetailListLook, string>> = { separator: styles.detailSeparator!, card: styles.detailCard! };

export function DetailList({ look, label, children, ...data }: DetailListProps) {
  return (
    <LookContext.Provider value={look}>
      <ul
        className={`${styles.list} ${LIST_LOOK[look]}`}
        data-detail-list={look}
        {...(label === undefined ? {} : { 'aria-label': label })}
        {...data}
      >
        {children}
      </ul>
    </LookContext.Provider>
  );
}

export function DetailRow({ look, title, lead, detail, href, pick, leading, field, badges, columns, actions, children, expansion, ...data }: DetailRowProps) {
  const inherited = useContext(LookContext);
  const drawn = look ?? inherited;
  const lines: readonly ReactNode[] = detail === undefined ? [] : Array.isArray(detail) ? detail : [detail];
  const titleClass = TITLE_LOOK[drawn];
  const main = (
    <span className={styles.main}>
      {title === undefined ? null : href === undefined ? (
        <span className={titleClass}>{title}</span>
      ) : (
        /* 🔨 A destination is a Link, so the browser's own open-in-new-tab reaches it; the row is not a click target. */
        <Link className={titleClass} to={href}>{title}</Link>
      )}
      {lead}
      {lines.map((line, index) =>
        line === null || line === undefined || line === false ? null : (
          <span key={index} className={`${styles.detail} ${DETAIL_LOOK[drawn]}`}>{line}</span>
        ),
      )}
      {field}
    </span>
  );
  const drawnColumns = columns ?? [];
  const rowClass = drawnColumns.length === 0 ? `${styles.row} ${ROW_LOOK[drawn]}` : `${styles.row} ${ROW_LOOK[drawn]} ${styles.rowColumns}`;
  return (
    <li className={rowClass} {...data}>
      {leading}
      {pick === undefined ? main : <label className={styles.pick}>{pick}{main}</label>}
      {drawnColumns.map(({ label, ariaLabel, value, ...attributes }, index) => (
        <span
          key={index}
          className={styles.column}
          /* 🔒 The stylesheet reads the label from here, once for every row, and shows it only where the row stacks. */
          {...(label === undefined || label === '' ? {} : { 'data-document-column': label })}
          {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
          {...attributes}
        >
          {value}
        </span>
      ))}
      {badges}
      {children}
      {actions}
      {expansion}
    </li>
  );
}
